/**
 * The small uncropped mockup that appears when an aspect button is clicked. Every viewer and thumbnail
 * crops to the centre, so nothing on screen ever shows a black band; this is the one place that does,
 * deliberately, so picking a shape the footage is not in shows its bars before anything is composed
 * (Dax, 2026-09-20). It says its piece for a few seconds and goes away again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ASPECT_PEEK_MS, type VideoFormat } from '@studio/contracts';

export function useAspectPeek(): { peek: VideoFormat | null; showPeek: (format: VideoFormat) => void } {
  const [peek, setPeek] = useState<VideoFormat | null>(null);
  const timer = useRef<number | null>(null);
  const clear = () => { if (timer.current !== null) window.clearTimeout(timer.current); };
  // Clicking the same button again simply restarts the clock on a mockup that is already up.
  const showPeek = useCallback((format: VideoFormat) => {
    setPeek(format);
    clear();
    timer.current = window.setTimeout(() => setPeek(null), ASPECT_PEEK_MS);
  }, []);
  useEffect(() => () => clear(), []);
  return { peek, showPeek };
}

export function AspectPeek({ format, posterUrl }: { format: VideoFormat | null; posterUrl: string | null }) {
  if (!format || !posterUrl) return null;
  return (
    <div className="aspect-peek" role="status" aria-label={`The clip in ${format}, uncropped`}>
      <div className={`aspect-peek-frame f-${format.replace(':', '-')}`}>
        <img className="aspect-peek-img" src={posterUrl} alt="" />
      </div>
      <span className="sub aspect-peek-label">{format} · whole frame</span>
    </div>
  );
}
